import React from 'react'
import {
    IAuditEventData,
    CustomerCustomMapLayerAuditSnapshotV1,
    MapLayerFileUploadedAuditSnapshotV1,
    MapLayerAuditSnapshotV2,
    MapLayerAuditSnapshotV3,
    MapLayerAuditSnapshotV4
} from '../../../api'
import { DetailField } from './DetailField'
import { UnavailableDetails } from './UnavailableDetails'
import { useDetailStyles } from './useDetailStyles'
import { resolveSnapshots } from './utils'
import { Box } from "cs-kevlar-react";
import { DetailArrayField } from './DetailArrayField'

interface CustomerCustomMapLayerDetailsProps {
    eventData: IAuditEventData
}

const renderFromSnapshotV1: React.FunctionComponent<IAuditEventData> = (eventData: IAuditEventData) => {
    const [original, updated] = resolveSnapshots<CustomerCustomMapLayerAuditSnapshotV1>(eventData, nameof<CustomerCustomMapLayerAuditSnapshotV1>())
    if (!original && !updated)
        return null

    const classes = useDetailStyles()
    const showDiff = !!original && !!updated

    return (
        <React.Fragment>
            <Box className={classes.fieldsContainer}>
                <DetailField
                    label="Name"
                    showDiff={showDiff}
                    original={original?.name}
                    updated={updated?.name}
                />
                <DetailField
                    label="Display Name"
                    showDiff={showDiff}
                    original={original?.displayName}
                    updated={updated?.displayName}
                />
                <DetailField
                    label="File Name"
                    showDiff={showDiff}
                    original={original?.fileName}
                    updated={updated?.fileName}
                />
                <DetailField
                    label="File Changed"
                    showDiff={false}
                    original="No"
                    updated={updated?.fileChanged ? "Yes" : "No"}
                />
                <DetailField
                    label="Notes"
                    showDiff={showDiff}
                    original={original?.notes}
                    updated={updated?.notes}
                />
            </Box>
        </React.Fragment>
    )
}

const renderFromFileUploadedSnapshotV1: React.FunctionComponent<IAuditEventData> = (eventData: IAuditEventData) => {
    const [_, snapshot] = resolveSnapshots<MapLayerFileUploadedAuditSnapshotV1>(eventData, nameof<MapLayerFileUploadedAuditSnapshotV1>())
    if (!snapshot)
        return null

    const classes = useDetailStyles()

    return (
        <React.Fragment>
            <Box className={classes.fieldsContainer}>
                <DetailField
                    label="Name"
                    original={snapshot.layerName}
                />
                <DetailField
                    label="Filename"
                    original={snapshot.fileName}
                />
                <DetailField
                    label="Result"
                    original={snapshot.result}
                />
                <DetailField
                    label="Details"
                    original={snapshot.details}
                />
            </Box>
        </React.Fragment>
    )
}

const renderFromSnapshotV2: React.FunctionComponent<IAuditEventData> = (eventData: IAuditEventData) => {
    const [original, updated] = resolveSnapshots<MapLayerAuditSnapshotV2>(eventData, nameof<MapLayerAuditSnapshotV2>())
    if (!original && !updated)
        return null

    const classes = useDetailStyles()
    const showDiff = !!original && !!updated

    return (
        <React.Fragment>
            <Box className={classes.fieldsContainer}>
                <DetailField
                    label="Name"
                    showDiff={showDiff}
                    original={original?.name}
                    updated={updated?.name}
                />
                <DetailField
                    label="Display Name"
                    showDiff={showDiff}
                    original={original?.displayName}
                    updated={updated?.displayName}
                />
                <DetailField
                    label="File Name"
                    showDiff={showDiff}
                    original={original?.fileName}
                    updated={updated?.fileName}
                />
                <DetailArrayField
                    label="Shared Agencies"
                    showDiff={showDiff}
                    original={Object.values(
                        original?.sharedLicensedAgencies ?? [],
                    )}
                    updated={Object.values(
                        updated?.sharedLicensedAgencies ?? [],
                    )}
                />
                <DetailField
                    label="Notes"
                    showDiff={showDiff}
                    original={original?.notes}
                    updated={updated?.notes}
                />
            </Box>
        </React.Fragment>
    )
}

const renderFromSnapshotV3: React.FunctionComponent<IAuditEventData> = (eventData: IAuditEventData) => {
    const [original, updated] = resolveSnapshots<MapLayerAuditSnapshotV3>(eventData, nameof<MapLayerAuditSnapshotV3>())
    if (!original && !updated)
        return null

    const classes = useDetailStyles()
    const showDiff = !!original && !!updated

    return (
        <React.Fragment>
            <Box className={classes.fieldsContainer}>
                <DetailField
                    label="Name"
                    showDiff={showDiff}
                    original={original?.name}
                    updated={updated?.name}
                />
                <DetailField
                    label="Display Name"
                    showDiff={showDiff}
                    original={original?.displayName}
                    updated={updated?.displayName}
                />
                <DetailArrayField
                    label="Shared Agencies"
                    showDiff={showDiff}
                    original={Object.values(
                        original?.sharedLicensedAgencies ?? [],
                    )}
                    updated={Object.values(
                        updated?.sharedLicensedAgencies ?? [],
                    )}
                />
                <DetailField
                    label="Notes"
                    showDiff={showDiff}
                    original={original?.notes}
                    updated={updated?.notes}
                />
            </Box>
        </React.Fragment>
    )
}

const renderFromSnapshotV4: React.FunctionComponent<IAuditEventData> = (eventData: IAuditEventData) => {
    const [original, updated] = resolveSnapshots<MapLayerAuditSnapshotV4>(eventData, nameof<MapLayerAuditSnapshotV4>())
    if (!original && !updated)
        return null

    const classes = useDetailStyles()
    const showDiff = !!original && !!updated

    return (
        <React.Fragment>
            <Box className={classes.fieldsContainer}>
                <DetailField
                    label="Name"
                    showDiff={showDiff}
                    original={original?.name}
                    updated={updated?.name}
                />
                <DetailField
                    label="Display Name"
                    showDiff={showDiff}
                    original={original?.displayName}
                    updated={updated?.displayName}
                />
                <DetailArrayField
                    label="Shared Agencies"
                    showDiff={showDiff}
                    original={Object.values(
                        original?.sharedLicensedAgencies ?? [],
                    )}
                    updated={Object.values(
                        updated?.sharedLicensedAgencies ?? [],
                    )}
                />
                <DetailField
                    label="Notes"
                    showDiff={showDiff}
                    original={original?.notes}
                    updated={updated?.notes}
                />
                <DetailField
                    label="File Uploaded"
                    showDiff={showDiff}
                    original="No"
                    updated={updated?.fileUploaded ? "Yes" : "No"}
                />
                <DetailField
                    label="File Name"
                    showDiff={showDiff}
                    original={original?.fileName}
                    updated={updated?.fileName}
                />
                <DetailField
                    label="File Size"
                    showDiff={showDiff}
                    original={original?.fileSize}
                    updated={updated?.fileSize}
                />
            </Box>
        </React.Fragment>
    )
}

export const CustomerCustomMapLayerDetails: React.FunctionComponent<CustomerCustomMapLayerDetailsProps> = ({
    eventData,
}) => {
    return (
        renderFromSnapshotV1(eventData) ??
        renderFromSnapshotV2(eventData) ??
        renderFromSnapshotV3(eventData) ??
        renderFromSnapshotV4(eventData) ??
        renderFromFileUploadedSnapshotV1(eventData) ??
        <UnavailableDetails />
    )
}
